"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";

import PharmacyHeader from "../../components/PharmacyHeader/PharmacyHeader";
import PharmacyList from "../../components/PharmacyList/PharmacyList";

import usePlaces from "../../hooks/usePlaces";
import { useTour } from "../../../appointments/hooks/useTour";
import { labTourSteps } from "../../pages/Lab/labTourConfig";

import "./Lab.css";

export default function Laboratory({ LayoutComponent = AppLayoutDoctor }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [shouldStartTour, setShouldStartTour] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(20); // initial items

  const { items, loading, error, loadCategory, selectPlace } = usePlaces();

  // Start tour if requested
  useEffect(() => {
    const hasGuide = searchParams.get("guide") === "true";
    const hasTour = searchParams.get("tour") === "lab";
    if (hasGuide || hasTour) {
      setTimeout(() => {
        const searchInput = document.querySelector('input[placeholder*="Search"]');
        const labList = document.querySelector(".lab-list-scroll");
        if (searchInput && labList) setShouldStartTour(true);
        else setTimeout(() => setShouldStartTour(true), 1500);
      }, 1000);
    }
  }, [searchParams]);

  useTour(shouldStartTour, labTourSteps);

  // Load category on mount
  useEffect(() => {
    loadCategory("analysis_labs", { limit: 50, skip: 0 });
  }, [loadCategory]);

  // Filtered + sliced for infinite scroll
  const filtered = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const q = (searchQuery || "").trim().toLowerCase();
    const filteredList = !q
      ? list
      : list.filter(
          (lab) =>
            (lab?.name || "").toLowerCase().includes(q) ||
            (lab?.address || "").toLowerCase().includes(q) ||
            (lab?.vicinity || "").toLowerCase().includes(q)
        );
    return filteredList.slice(0, visibleCount); // only show visibleCount
  }, [items, searchQuery, visibleCount]);

  // Infinite scroll: load more on scroll
  useEffect(() => {
    const container = document.querySelector(".lab-list-scroll");
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 10) {
        setVisibleCount((prev) => prev + 20); // load 20 more
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

	  return (
    <LayoutComponent>
      <main className="lab-main lab--listOnly">
	        <div className="lab-container">
	          <PharmacyHeader
	            title={t("locators.lab.title")}
	            subtitle={t("locators.lab.subtitle")}
            showTabs={false}
            enableFilters={false}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            activeTab="Laboratory"
            setActiveTab={() => {}}
          />

          <section className="lab-list-card">
	            <div className="lab-list-scroll">
	              {loading && (
	                <p className="lab-empty">{t("locators.lab.loading")}</p>
	              )}
	              {!loading && error && (
	                <p className="lab-empty">
	                  {t("locators.lab.failed", { error: String(error) })}
	                </p>
	              )}
	              {!loading && !error && filtered.length === 0 ? (
	                <p className="lab-empty">{t("locators.lab.empty")}</p>
	              ) : (
                <PharmacyList
                  pharmacies={filtered}
                  onPharmacyClick={(lab) => selectPlace(lab)}
                  selectedPharmacy={null}
                />
              )}
            </div>
          </section>
        </div>
      </main>
    </LayoutComponent>
  );
}
